/**
 * Centralized API Client using TanStack Query
 *
 * Provides:
 * - Authentication management
 * - Tenant context injection
 * - Request cancellation
 * - Retry logic with exponential backoff
 * - Comprehensive error handling
 * - Type-safe hooks for queries and mutations
 */

import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
} from '@tanstack/react-query';
import { getApiBaseUrl } from './apiConfig';
import { isDevelopment, getDevAdminToken } from './featureFlags';
import { handleQueryError, AppError, createNetworkError } from './error-handling';
import {
  type BackendSuccessResponse,
  type BackendErrorResponse,
  backendErrorResponseSchema,
} from '@/schemas/common/api.schemas';

// ============================================================================
// Configuration
// ============================================================================

const API_CONFIG = {
  baseURL: getApiBaseUrl(),
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // For cookies if needed
} as const;

// ============================================================================
// 401 Unauthorized Handler (Logout & Redirect)
// ============================================================================

let onUnauthorizedCallback: (() => void) | null = null;

/**
 * Register a callback to be called when a 401 Unauthorized response is received.
 * This should be called from AuthProvider to trigger logout and redirect.
 *
 * @example
 * // In AuthProvider
 * useEffect(() => {
 *   registerUnauthorizedHandler(() => {
 *     signOut();
 *     navigate('/login');
 *   });
 * }, [signOut, navigate]);
 */
export function registerUnauthorizedHandler(callback: () => void): void {
  onUnauthorizedCallback = callback;
}

/**
 * Unregister the unauthorized handler
 */
export function unregisterUnauthorizedHandler(): void {
  onUnauthorizedCallback = null;
}

/**
 * Handle 401 Unauthorized responses by calling the registered callback
 */
function handleUnauthorized(): void {
  if (onUnauthorizedCallback) {
    onUnauthorizedCallback();
  }
}

// ============================================================================
// Authentication & Tenant Token Management
// ============================================================================

/**
 * Get authentication token from localStorage/sessionStorage
 */
export function getAuthToken(rememberMe?: boolean): string | null {
  // If rememberMe is explicitly provided, check only that storage
  if (rememberMe !== undefined) {
    const storage = rememberMe ? localStorage : sessionStorage;
    return storage.getItem('authToken');
  }
  return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
}

/**
 * Set authentication token in storage based on rememberMe preference
 * @param token - The authentication token to store
 * @param rememberMe - If true, stores in localStorage (persists across sessions)
 *                     If false, stores in sessionStorage (clears on tab close)
 */
export function setAuthToken(token: string, rememberMe: boolean = false): void {
  const storage = rememberMe ? localStorage : sessionStorage;

  // Clear from the other storage to avoid conflicts
  if (rememberMe) {
    sessionStorage.removeItem('authToken');
  } else {
    localStorage.removeItem('authToken');
  }
  storage.setItem('authToken', token);
}

/**
 * Clear authentication token from both localStorage and sessionStorage
 */
export function clearAuthToken(): void {
  localStorage.removeItem('authToken');
  sessionStorage.removeItem('authToken');
}

/**
 * Get selected tenant ID from localStorage
 */
export function getSelectedTenantId(): string | null {
  return localStorage.getItem('selected_tenant_id');
}

/**
 * Set selected tenant ID in localStorage
 */
export function setSelectedTenantId(tenantId: string): void {
  localStorage.setItem('selected_tenant_id', tenantId);
}

/**
 * Clear selected tenant ID from localStorage
 */
export function clearSelectedTenantId(): void {
  localStorage.removeItem('selected_tenant_id');
}

// ============================================================================
// TypeScript Types
// ============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface ApiErrorResponse {
  success: false;
  error: {
    message: string;
    code: string;
    details?: unknown;
  };
}

export interface FetchOptions {
  method?: string;
  headers?: Record<string, string> | Headers;
  signal?: AbortSignal;
  credentials?: 'include' | 'omit' | 'same-origin';
  mode?: 'cors' | 'navigate' | 'no-cors' | 'same-origin';
  cache?: 'default' | 'force-cache' | 'no-cache' | 'no-store' | 'only-if-cached' | 'reload';
  redirect?: 'error' | 'follow' | 'manual';
  referrer?: string;
  referrerPolicy?:
    | ''
    | 'no-referrer'
    | 'no-referrer-when-downgrade'
    | 'origin'
    | 'origin-when-cross-origin'
    | 'same-origin'
    | 'strict-origin'
    | 'strict-origin-when-cross-origin'
    | 'unsafe-url';
  integrity?: string;
  keepalive?: boolean;
  timeout?: number;
  skipAuth?: boolean;
  skipTenant?: boolean;
  body?: unknown; // Allow any type for body, will be JSON stringified
}

// ============================================================================
// Request Headers Builder
// ============================================================================

/**
 * Build request headers with authentication and tenant context
 */
function buildHeaders(options: FetchOptions = {}): Headers {
  const headers = new Headers(API_CONFIG.headers);

  // Add authentication token if available (unless skipAuth is true)
  if (!options.skipAuth) {
    const authToken = getAuthToken();

    // Use dev token in development if no auth token exists
    const token = authToken || getDevAdminToken();

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Add tenant context if available (unless skipTenant is true)
  if (!options.skipTenant) {
    const tenantId = getSelectedTenantId();
    if (tenantId) {
      headers.set('X-Tenant-ID', tenantId);
    }
  }

  // Merge with custom headers from options
  if (options.headers) {
    const customHeaders = new Headers(options.headers);
    customHeaders.forEach((value, key) => {
      headers.set(key, value);
    });
  }

  return headers;
}

// ============================================================================
// Fetch Wrapper with Timeout
// ============================================================================

/**
 * Fetch with timeout support
 */
async function fetchWithTimeout(url: string, options: FetchOptions = {}): Promise<Response> {
  const timeout = options.timeout || API_CONFIG.timeout;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    // Merge abort signals if provided
    const signal = options.signal
      ? // Create a new signal that aborts when either the timeout or provided signal aborts
        AbortSignal.any([controller.signal, options.signal])
      : controller.signal;

    // Build fetch options
    const fetchOptions = {
      method: options.method,
      headers: options.headers,
      credentials: options.credentials,
      mode: options.mode,
      cache: options.cache,
      redirect: options.redirect,
      referrer: options.referrer,
      referrerPolicy: options.referrerPolicy,
      integrity: options.integrity,
      keepalive: options.keepalive,
      signal,
      body: options.body as string | undefined,
    };

    const response = await fetch(url, fetchOptions);

    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

// ============================================================================
// Backend Response Transformation
// ============================================================================

/**
 * Type guard to detect new backend success response format
 */
function isBackendSuccessResponse(response: unknown): response is BackendSuccessResponse<unknown> {
  return (
    typeof response === 'object' &&
    response !== null &&
    'data' in response &&
    'meta' in response &&
    'links' in response
  );
}

/**
 * Type guard to detect new backend error response format
 */
function isBackendErrorResponse(response: unknown): response is BackendErrorResponse {
  return backendErrorResponseSchema.safeParse(response).success;
}

/**
 * Transform backend response format to expected service format
 * Automatically detects entity type from endpoint and maps appropriately
 *
 * @param response - The raw backend response
 * @param endpoint - The API endpoint used (for entity name detection)
 * @returns Transformed response in the expected service format
 *
 * @example
 * // Backend returns: { data: [...], meta: {...}, links: {...}, pagination: {...} }
 * // Transforms to: { assets: [...], total: 50, page: 1, limit: 20, hasMore: true }
 */
function transformBackendResponse<T>(response: unknown, endpoint: string): T {
  // If not backend format, return as-is (backwards compatibility)
  if (!isBackendSuccessResponse(response)) {
    return response as T;
  }

  const backendResponse = response as BackendSuccessResponse<unknown>;

  // Handle list responses (data is array)
  if (Array.isArray(backendResponse.data)) {
    // Extract entity name from the last path segment of the endpoint.
    // Handles nested resources: /api/assets, /api/factories/{id}/assets,
    // /api/tenants/{id}/roles, etc.
    const [pathWithoutQuery] = endpoint.split('?');
    const segments = pathWithoutQuery.split('/').filter(Boolean);
    const entityName = segments[segments.length - 1] ?? 'items';

    const result: Record<string, unknown> = {
      [entityName]: backendResponse.data,
    };

    // Flatten pagination if present
    if (backendResponse.pagination) {
      result.total = backendResponse.pagination.total;
      result.page = backendResponse.pagination.page;
      result.limit = backendResponse.pagination.limit;
      result.hasMore = backendResponse.pagination.hasNext;
      result.totalPages = backendResponse.pagination.totalPages;
      result.hasPrev = backendResponse.pagination.hasPrev;
    }

    // Preserve meta in dev for debugging
    if (isDevelopment()) {
      result._meta = backendResponse.meta;
    }

    return result as T;
  }

  // Single entity - unwrap data field
  return backendResponse.data as T;
}

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Transform fetch errors into AppError format
 */
async function handleFetchError(error: unknown, url: string): Promise<never> {
  // Handle AbortError (request cancellation)
  if (error instanceof Error && error.name === 'AbortError') {
    throw createNetworkError('Request was cancelled');
  }

  // Handle network errors
  if (error instanceof TypeError) {
    throw createNetworkError('Network request failed. Please check your connection.');
  }

  // Handle timeout errors
  if (error instanceof Error && error.message.includes('timeout')) {
    throw createNetworkError('Request timeout. Please try again.');
  }

  // Handle other errors via existing error handler
  throw handleQueryError(error, ['api', 'fetch', url]);
}

/**
 * Parse API error response
 * Handles both new backend error format and legacy format for backwards compatibility
 */
async function parseErrorResponse(response: Response): Promise<AppError> {
  try {
    const data = await response.json();

    // Check if it's new backend error format
    if (isBackendErrorResponse(data)) {
      const { error, meta } = data as BackendErrorResponse;
      const category = error.statusCode >= 500 ? 'SERVER' : 'VALIDATION';
      const appError = new AppError(error.message, category);

      // Add additional properties including requestId for debugging
      Object.assign(appError, {
        code: error.code,
        statusCode: error.statusCode,
        details: error.details,
        requestId: meta.requestId,
      });

      return appError;
    }

    // Fallback: old format handling (backwards compatibility)
    const apiErrorData = data as ApiErrorResponse;
    const category = response.status >= 500 ? 'SERVER' : 'VALIDATION';
    const error = new AppError(apiErrorData.error?.message || 'An error occurred', category);

    Object.assign(error, {
      code: apiErrorData.error?.code || 'UNKNOWN_ERROR',
      statusCode: response.status,
      details: apiErrorData.error?.details,
    });

    return error;
  } catch {
    // If JSON parsing fails, create a generic error
    const category = response.status >= 500 ? 'SERVER' : 'VALIDATION';
    const error = new AppError(response.statusText || 'An error occurred', category);
    Object.assign(error, {
      code: 'HTTP_ERROR',
      statusCode: response.status,
    });
    return error;
  }
}

// ============================================================================
// Centralized Fetch Wrapper
// ============================================================================

/**
 * On 401, refresh the Firebase ID token and retry the request once.
 * Returns the parsed response if the retry succeeds, or null if it fails
 * (caller should then call handleUnauthorized).
 */
async function tryTokenRefreshAndRetry<T>(
  url: string,
  endpoint: string,
  options: FetchOptions
): Promise<T | null> {
  try {
    const { auth } = await import('@/lib/firebase');
    const { currentUser } = auth;
    if (!currentUser) {
      return null;
    }

    // Force-refresh the Firebase ID token
    const freshToken = await currentUser.getIdToken(true);

    // Persist the refreshed token using the same storage as before
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    setAuthToken(freshToken, rememberMe);

    // Retry with updated headers (buildHeaders will pick up the new token)
    const retryHeaders = buildHeaders(options);
    const retryBody =
      options.body && typeof options.body !== 'string'
        ? JSON.stringify(options.body)
        : (options.body as string | undefined);

    const retryResponse = await fetchWithTimeout(url, {
      ...options,
      headers: retryHeaders,
      body: retryBody,
    });

    if (!retryResponse.ok) {
      return null;
    }
    if (retryResponse.status === 204) {
      return undefined as T;
    }

    const rawData = await retryResponse.json();
    return transformBackendResponse<T>(rawData, endpoint);
  } catch {
    return null;
  }
}

/**
 * Fetch wrapper with authentication, tenant context, and error handling
 */
export async function fetchWithAuth<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_CONFIG.baseURL}${endpoint}`;

  try {
    // Build headers with auth and tenant context
    const headers = buildHeaders(options);

    // Prepare request body
    const body =
      options.body && typeof options.body !== 'string'
        ? JSON.stringify(options.body)
        : options.body;

    // Make request with timeout
    const response = await fetchWithTimeout(url, {
      ...options,
      headers,
      body: body as string,
    });

    // Handle non-OK responses
    if (!response.ok) {
      // On 401, silently refresh the Firebase token and retry once before logging out.
      // This prevents unexpected logouts caused by a locally-expired cached token
      // while the Firebase session is still valid.
      if (response.status === 401) {
        const retried = await tryTokenRefreshAndRetry<T>(url, endpoint, options);
        if (retried !== null) {
          return retried;
        }
        handleUnauthorized();
      }

      const appError = await parseErrorResponse(response);
      throw appError;
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return undefined as T;
    }

    // Parse JSON response and transform from new backend format
    const rawData = await response.json();
    const transformedData = transformBackendResponse<T>(rawData, endpoint);
    return transformedData;
  } catch (error) {
    // If it's already an AppError (from parseErrorResponse), re-throw it as-is
    if (error instanceof AppError) {
      throw error;
    }
    // Otherwise, transform it via handleFetchError
    throw await handleFetchError(error, url);
  }
}

/**
 * Fetch wrapper for public endpoints (no authentication)
 */
export async function fetchWithoutAuth<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  return fetchWithAuth<T>(endpoint, {
    ...options,
    skipAuth: true,
    skipTenant: true,
  });
}

// ============================================================================
// TanStack Query Hooks - Authenticated Requests
// ============================================================================

/**
 * Hook for GET requests with authentication
 *
 * @example
 * const { data, isLoading, error } = useAuthQuery(['users'], '/api/users');
 */
export function useAuthQuery<T>(
  queryKey: QueryKey,
  endpoint: string,
  options?: Omit<UseQueryOptions<T, AppError>, 'queryKey' | 'queryFn'>
): ReturnType<typeof useQuery<T, AppError>> {
  return useQuery<T, AppError>({
    queryKey,
    queryFn: ({ signal }) => fetchWithAuth<T>(endpoint, { signal }),
    ...options,
  });
}

/**
 * Hook for POST/PUT/PATCH/DELETE mutations with authentication
 *
 * @example
 * const mutation = useAuthMutation('/api/users', { method: 'POST' });
 * mutation.mutate({ name: 'John Doe' });
 */
export function useAuthMutation<TData = unknown, TVariables = unknown>(
  endpoint: string,
  options?: UseMutationOptions<TData, AppError, TVariables> & {
    method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  }
): ReturnType<typeof useMutation<TData, AppError, TVariables>> {
  const { method = 'POST', ...mutationOptions } = options || {};

  return useMutation<TData, AppError, TVariables>({
    mutationFn: (variables: TVariables) =>
      fetchWithAuth<TData>(endpoint, {
        method,
        body: variables,
      }),
    ...mutationOptions,
  });
}

// ============================================================================
// TanStack Query Hooks - Public Requests
// ============================================================================

/**
 * Hook for public GET requests (no authentication)
 *
 * @example
 * const { data, isLoading } = usePublicQuery(['public-data'], '/api/public/data');
 */
export function usePublicQuery<T>(
  queryKey: QueryKey,
  endpoint: string,
  options?: Omit<UseQueryOptions<T, AppError>, 'queryKey' | 'queryFn'>
): ReturnType<typeof useQuery<T, AppError>> {
  return useQuery<T, AppError>({
    queryKey,
    queryFn: ({ signal }) => fetchWithoutAuth<T>(endpoint, { signal }),
    ...options,
  });
}

/**
 * Hook for public POST/PUT/PATCH/DELETE mutations (no authentication)
 *
 * @example
 * const mutation = usePublicMutation('/api/public/subscribe', { method: 'POST' });
 * mutation.mutate({ email: 'user@example.com' });
 */
export function usePublicMutation<TData = unknown, TVariables = unknown>(
  endpoint: string,
  options?: UseMutationOptions<TData, AppError, TVariables> & {
    method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  }
): ReturnType<typeof useMutation<TData, AppError, TVariables>> {
  const { method = 'POST', ...mutationOptions } = options || {};

  return useMutation<TData, AppError, TVariables>({
    mutationFn: (variables: TVariables) =>
      fetchWithoutAuth<TData>(endpoint, {
        method,
        body: variables,
      }),
    ...mutationOptions,
  });
}

// ============================================================================
// Utility Exports
// ============================================================================

/**
 * Get full API URL for a given endpoint
 */
export function getApiUrl(endpoint: string): string {
  return endpoint.startsWith('http') ? endpoint : `${API_CONFIG.baseURL}${endpoint}`;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}
